import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal, Button, Form, Alert, Card } from 'react-bootstrap';
import sportPositions from '../../data/sportPositions.json';
import { useGetSquadDataQuery } from '../../slices/squadDataApiSlice';
import { FaQuestionCircle } from 'react-icons/fa';

const AddSquadeDataModal = ({ show, onHide, onAddPlayer }) => {
  const [playerName, setPlayerName] = useState('');
  const [position, setPosition] = useState('');
  const [team, setTeam] = useState('');
  const [validationError, setValidationError] = useState('');
  const playerNameRef = useRef(null);
  const teamRef = useRef(null);
  const positionRef = useRef(null);
  const submitRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);


  const tutorialSteps = useMemo(
    () => [
      {
        title: 'Player Name',
        body: 'Enter the player name exactly as you want it to appear in shot entry and reports. Keeping names consistent matters because the analysis groups shots by this value.',
        ref: playerNameRef,
      },
      {
        title: 'Team',
        body: 'Assign the player to the correct team. In this app, the team link is what makes that player available in the Add Shot player list when that team is selected.',
        ref: teamRef,
      },
      {
        title: 'Position',
        body: 'Choose the player position from the Gaelic football list. This helps keep the squad data structured and ready for later filtering or role-based analysis.',
        ref: positionRef,
      },
      {
        title: 'Add Player',
        body: 'When the details are correct, save the new player here. The player will then appear in Squad Data immediately and become available for shot logging.',
        ref: submitRef,
      },
    ],
    [],
  );

  const updateTutorialRect = useCallback(() => {
    if (!showTutorial) {
      setTutorialRect(null);
      return;
    }
    const step = tutorialSteps[tutorialStepIndex];
    const node = step?.ref?.current;
    if (!node) {
      setTutorialRect(null);
      return;
    }
    const rect = node.getBoundingClientRect();
    setTutorialRect({
      top: Math.max(8, rect.top - 8),
      left: Math.max(8, rect.left - 8),
      width: rect.width + 16,
      height: rect.height + 16,
    });
  }, [showTutorial, tutorialStepIndex, tutorialSteps]);

  const { data: squadData = { entries: [] } } = useGetSquadDataQuery();
  const teamOptions = useMemo(() => {
    const allTeams = (squadData.entries || []).map((entry) => entry.team);
    return [...new Set(allTeams.filter(Boolean).map((team) => String(team).trim()))].sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
  }, [squadData.entries]);

  useEffect(() => {
    if (!show) {
      setPlayerName('');
      setPosition('');
      setTeam('');
      setValidationError('');
      setShowTutorial(false);
      setTutorialStepIndex(0);
      setTutorialRect(null);
    }
  }, [show]);

  useEffect(() => {
    if (!showTutorial) return undefined;
    const step = tutorialSteps[tutorialStepIndex];
    step?.ref?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    const refresh = () => updateTutorialRect();
    refresh();
    const timeoutId = window.setTimeout(refresh, 220);
    const secondTimeoutId = window.setTimeout(refresh, 420);
    const intervalId = window.setInterval(refresh, 70);
    const stopIntervalId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, 800);
    window.addEventListener('resize', refresh);
    window.addEventListener('scroll', refresh, true);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(secondTimeoutId);
      window.clearTimeout(stopIntervalId);
      window.clearInterval(intervalId);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('scroll', refresh, true);
    };
  }, [showTutorial, tutorialStepIndex, tutorialSteps, updateTutorialRect]);

  const startTutorial = () => {
    setTutorialStepIndex(0);
    setShowTutorial(true);
  };

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialRect(null);
  };

  const nextTutorialStep = () => {
    if (tutorialStepIndex >= tutorialSteps.length - 1) {
      closeTutorial();
      return;
    }
    setTutorialStepIndex((prev) => prev + 1);
  };

  const prevTutorialStep = () => {
    setTutorialStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const getTutorialCardStyle = () => {
    if (!tutorialRect) return {};
    const cardWidth = Math.min(360, window.innerWidth - 32);
    const gap = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placeBelow =
      tutorialRect.top + tutorialRect.height + 220 + gap < viewportHeight;
    const top = placeBelow
      ? tutorialRect.top + tutorialRect.height + gap
      : Math.max(16, tutorialRect.top - 220 - gap);
    const preferredLeft = tutorialRect.left;
    const maxLeft = Math.max(16, viewportWidth - cardWidth - 16);
    const left = Math.min(Math.max(16, preferredLeft), maxLeft);
    return {
      position: 'fixed',
      top,
      left,
      width: cardWidth,
      zIndex: 1062,
      boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35)',
      transition: 'top 160ms ease, left 160ms ease',
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!playerName.trim() || !position.trim() || !team.trim()) {
      setValidationError('All fields are required.');
      return;
    }

    setValidationError('');
    onAddPlayer({ playerName, position, team });
    onHide();
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered contentClassName="bg-dark text-white">
        <Modal.Header closeButton closeVariant="white" className="border-secondary">
          <div className="d-flex w-100 align-items-center justify-content-between me-3">
            <Modal.Title>Add New Player</Modal.Title>
            <Button
              variant="outline-light"
              size="sm"
              type="button"
              onClick={startTutorial}
              className="d-inline-flex align-items-center gap-2"
            >
              <FaQuestionCircle />
              Help
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="text-white">
          <Form onSubmit={handleSubmit}>
          {validationError && <Alert className="app-empty-state mb-3">{validationError}</Alert>}
          <Form.Group controlId="playerName" className="mb-3" ref={playerNameRef}>
            <Form.Label>Player Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter player's name"
              value={playerName}
              onChange={(e) => {
                setPlayerName(e.target.value);
                if (validationError) setValidationError('');
              }}
              autoComplete="off"
              required
            />
          </Form.Group>

          <Form.Group controlId="team" className="mb-3" ref={teamRef}>
            <Form.Label>Team</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter team name"
              value={team}
              onChange={(e) => {
                setTeam(e.target.value);
                if (validationError) setValidationError('');
              }}
              list="team-suggestions"
              autoComplete="off"
              required
            />
            <datalist id="team-suggestions">
              {teamOptions.map((teamName, idx) => (
                <option key={idx} value={teamName} />
              ))}
            </datalist>
          </Form.Group>

          <Form.Group controlId="position" className="mb-3" ref={positionRef}>
            <Form.Label>Position</Form.Label>
            <Form.Select
              value={position}
              onChange={(e) => {
                setPosition(e.target.value);
                if (validationError) setValidationError('');
              }}
              required
            >
              <option value="">Select Position</option>
              {sportPositions['Gaelic'].map((pos, index) => (
                <option key={index} value={pos}>
                  {pos}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button
            variant="warning"
            className="text-dark fw-semibold"
            type="submit"
            onClick={handleSubmit}
            ref={submitRef}
          >
            Add Player
          </Button>
        </Modal.Footer>
      </Modal>
      {showTutorial && (
        <>
          {tutorialRect ? (
            <>
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: tutorialRect.top,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: 0,
                  width: tutorialRect.left,
                  height: tutorialRect.height,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: tutorialRect.left + tutorialRect.width,
                  width: `calc(100vw - ${tutorialRect.left + tutorialRect.width}px)`,
                  height: tutorialRect.height,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                onClick={closeTutorial}
                style={{
                  position: 'fixed',
                  top: tutorialRect.top + tutorialRect.height,
                  left: 0,
                  width: '100vw',
                  height: `calc(100vh - ${tutorialRect.top + tutorialRect.height}px)`,
                  background: 'rgba(8, 12, 18, 0.72)',
                  zIndex: 1060,
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: tutorialRect.top,
                  left: tutorialRect.left,
                  width: tutorialRect.width,
                  height: tutorialRect.height,
                  border: '2px solid #f59e0b',
                  borderRadius: 12,
                  pointerEvents: 'none',
                  zIndex: 1061,
                  transition:
                    'top 160ms ease, left 160ms ease, width 160ms ease, height 160ms ease',
                }}
              />
              <Card className="border-warning" style={getTutorialCardStyle()}>
                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                  <strong>
                    {tutorialStepIndex + 1} / {tutorialSteps.length}{' '}
                    {tutorialSteps[tutorialStepIndex].title}
                  </strong>
                  <Button
                    variant="link"
                    className="text-dark p-0 text-decoration-none fw-semibold"
                    onClick={closeTutorial}
                  >
                    Close
                  </Button>
                </Card.Header>
                <Card.Body className="bg-dark text-white">
                  <p className="mb-3">{tutorialSteps[tutorialStepIndex].body}</p>
                  <div className="d-flex justify-content-between gap-2">
                    <Button
                      variant="outline-light"
                      size="sm"
                      disabled={tutorialStepIndex === 0}
                      onClick={prevTutorialStep}
                    >
                      Back
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      className="text-dark fw-semibold"
                      onClick={nextTutorialStep}
                    >
                      {tutorialStepIndex === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div
              onClick={closeTutorial}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(8, 12, 18, 0.72)',
                zIndex: 1060,
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default AddSquadeDataModal;
